import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { handleOnSettled } from '@utils/formHelpers';
import { localStore } from '@utils/localStore';
import { useSignIn } from '@services/auth/loginUser';
import { useErrors } from '@hooks/errors';
import { updateSlug } from '@hooks/slug/helpers';
import { LoginFormValues } from './types';
import { defaultValues, formSchema } from './constants';
import { FormProps } from '@forms/types';
import { SignInData, SignInResp } from '@services/auth/types';
import { createSignInExchange } from '@services/auth/signInExchange';
import { getAccess } from '@hooks/access/helpers';
import posthog from 'posthog-js';
import { useState } from 'react';

export const useLoginForm = ({ successHandler }: FormProps) => {
  const handleErrors = useErrors();
  const [isFinalizing, setIsFinalizing] = useState(false);

  const form = useForm<LoginFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const { setError } = form;
  const updateAuthData = async ({ id, primaryRole, tenant }: SignInData): Promise<boolean> => {
    const item = localStore.getItem(`app-${!tenant?.slug ? 'admin' : 'user'}`);
    const parsed = typeof item === 'string' ? (JSON.parse(item) as Record<string, string>) : item;
    if (!tenant?.slug) {
      localStore.updateItem('app-admin', { ...parsed, id });
      return true;
    }
    const access = getAccess(primaryRole, []);
    if (access.fieldUser) {
      localStore.updateItem('app-user', { ...parsed, id });
      localStore.updateItem('app-foreman', { enabled: true });
      return true;
    }
    const { data } = await createSignInExchange();
    updateSlug(tenant.slug, data.code);
    return false;
  };

  const callback = (resp: SignInResp) => {
    const errors = resp.errors || [];
    handleOnSettled<SignInResp>(
      resp,
      (data) => {
        const signInData = data as unknown as SignInData;
        posthog?.identify(signInData.id, { primary_role: signInData.primaryRole });
        posthog?.capture('user_logged_in', { primary_role: signInData.primaryRole });
        setIsFinalizing(true);
        void updateAuthData(signInData).then((shouldNavigate) => {
          if (shouldNavigate) successHandler();
        });
      },
      () =>
        handleErrors({ root: errors?.[0] || '', ...errors }, (key, message) =>
          setError(key as keyof LoginFormValues, { message }),
        ),
    );
  };

  const { mutate, isLoading } = useSignIn({ callback });

  const onSubmit = async (values: LoginFormValues) => {
    mutate(values);
  };

  return {
    form,
    onSubmit,
    locked: isLoading || isFinalizing,
  };
};
